/* =============================================================
   ProcessConfigSection.jsx — Process configuration UI
   -------------------------------------------------------------
   Renders inside Settings.jsx. Lets the IO manager configure
   the exchange process for their university.

   Two panels (Incoming / Outgoing), each with:
     - Empty state: template picker + Apply button
     - Configured state: editable step list + Add step row
       + Reset-to-template option

   Each step row supports:
     - Rename (click label to edit, saves on blur)
     - Link / unlink a document type (dropdown, saves on change)
     - Reorder (↑ / ↓ arrows)
     - Delete (trash icon, appears on hover)

   No separate Save button — every change writes to Supabase
   immediately (auto-save pattern).
   ============================================================= */

import { useState } from 'react';
import { useProcessConfig }   from '../hooks/useProcessConfig';
import { useProcessTemplates } from '../hooks/useProcessTemplates';
import { useDocumentTypes }   from '../hooks/useDocumentTypes';
import { useSemesters }       from '../hooks/useSemesters';
import { DEGREE_LEVELS, STUDENT_CATEGORIES } from '../lib/studentCategory';


/* ─── ADD DOC SELECT ─────────────────────────────────────────── */
// Compact inline dropdown to link one more doc to a step.
// Filtered: already-linked doc items are hidden.
function AddDocSelect({ step, docTypes, onAdd }) {
  const linked    = new Set(
    (step.process_step_documents ?? [])
      .filter(psd => psd.item_type !== 'task')
      .map(psd => psd.document_type_id)
  );
  const available = docTypes.filter(d => !linked.has(d.id));
  if (available.length === 0) return null;

  return (
    <select
      value=""
      onChange={e => { if (e.target.value) onAdd(step.id, e.target.value); }}
      className="px-2 py-0.5 text-[11px] border border-dashed border-indigo-200 rounded-full text-indigo-400 bg-white focus:outline-none cursor-pointer hover:border-indigo-400 transition-colors"
    >
      <option value="">＋ add doc</option>
      {available.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
    </select>
  );
}


/* ─── ADD TASK SELECT ────────────────────────────────────────── */
// Compact inline dropdown to link a task type to a step.
// Filtered: already-linked tasks are hidden.
function AddTaskSelect({ step, taskTypes, onAdd }) {
  const linked    = new Set(
    (step.process_step_documents ?? [])
      .filter(psd => psd.item_type === 'task')
      .map(psd => psd.document_type_id)
  );
  const available = taskTypes.filter(t => !linked.has(t.id));

  // No task types exist at all
  if (taskTypes.length === 0) {
    return (
      <span
        className="px-2 py-0.5 text-[11px] border border-dashed border-slate-200 rounded-full text-slate-300 bg-white cursor-not-allowed select-none"
        title="Create task types first in Document Types → Tasks tab"
      >
        ＋ add task
      </span>
    );
  }

  // All task types already linked to this step
  if (available.length === 0) {
    return (
      <span
        className="px-2 py-0.5 text-[11px] border border-dashed border-slate-200 rounded-full text-slate-300 bg-white cursor-not-allowed select-none"
        title="All task types are already added to this step"
      >
        ＋ add task
      </span>
    );
  }

  return (
    <select
      value=""
      onChange={e => { if (e.target.value) onAdd(step.id, e.target.value); }}
      className="px-2 py-0.5 text-[11px] border border-dashed border-emerald-200 rounded-full text-emerald-500 bg-white focus:outline-none cursor-pointer hover:border-emerald-400 transition-colors"
    >
      <option value="">＋ add task</option>
      {available.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );
}


/* ─── STEP ROW ───────────────────────────────────────────────── */
function StepRow({ step, idx, total, docTypes, taskTypes, onUpdate, onDelete, onMove, onAddDoc, onAddTask, onRemoveDoc, onUpdateDocDeadline }) {
  const [label,   setLabel]   = useState(step.label);
  const [editing, setEditing] = useState(false);

  // Days until a date string for colour coding
  const daysUntil = (d) => {
    if (!d) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.round((new Date(d + 'T00:00:00') - today) / 86_400_000);
  };

  const deadlineColor = (days) =>
    days === null ? 'text-slate-400'
    : days < 0   ? 'text-red-500'
    : days <= 7  ? 'text-red-500'
    : days <= 14 ? 'text-amber-500'
    : 'text-slate-400';

  const deadlineLabel = (days) =>
    days === null ? '' : days < 0 ? 'Overdue' : days === 0 ? 'Today' : `${days}d left`;

  // Save label on blur — only if changed.
  const handleLabelBlur = () => {
    setEditing(false);
    const trimmed = label.trim();
    if (trimmed && trimmed !== step.label) {
      onUpdate(step.id, { label: trimmed });
    } else {
      setLabel(step.label);
    }
  };

  const docs      = step.process_step_documents ?? [];
  const canDelete = docs.length > 1; // enforce min 1 item per step

  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 overflow-hidden group/card">

      {/* ── Step header ─────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-3 py-2.5">

        {/* ↑ / ↓ reorder buttons */}
        <div className="flex flex-col gap-0 flex-shrink-0">
          <button
            onClick={() => onMove(step.id, 'up')}
            disabled={idx === 0}
            className="text-slate-300 hover:text-indigo-500 disabled:invisible transition-colors"
          >
            <span className="material-symbols-outlined text-[15px] leading-none">expand_less</span>
          </button>
          <button
            onClick={() => onMove(step.id, 'down')}
            disabled={idx === total - 1}
            className="text-slate-300 hover:text-indigo-500 disabled:invisible transition-colors"
          >
            <span className="material-symbols-outlined text-[15px] leading-none">expand_more</span>
          </button>
        </div>

        {/* Step number badge */}
        <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">
          {idx + 1}
        </span>

        {/* Editable label */}
        {editing ? (
          <input
            autoFocus
            value={label}
            onChange={e => setLabel(e.target.value)}
            onBlur={handleLabelBlur}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
            className="flex-1 px-2 py-1 text-sm text-[#0b1c30] bg-white border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 transition"
          />
        ) : (
          <span
            onClick={() => setEditing(true)}
            className="flex-1 px-2 py-1 text-sm font-semibold text-[#0b1c30] cursor-text rounded-lg hover:bg-white/70 transition-colors"
            title="Click to rename"
          >
            {step.label}
          </span>
        )}

        {/* Delete button — visible on card hover */}
        <button
          onClick={() => {
            if (window.confirm(`Delete step "${step.label}"?`)) onDelete(step.id);
          }}
          className="opacity-0 group-hover/card:opacity-100 text-slate-300 hover:text-red-500 transition-all flex-shrink-0"
          title="Delete step"
        >
          <span className="material-symbols-outlined text-[18px]">delete</span>
        </button>
      </div>

      {/* ── Items sub-list (docs + tasks) ───────────────────── */}
      <div className="border-t border-slate-100 px-3 py-2 space-y-1.5">
        {docs.map(psd => {
          const isTask = psd.item_type === 'task';
          const days   = daysUntil(psd.deadline_date);
          return (
            <div key={psd.id} className="flex items-center gap-2 pl-8">
              <span className={`material-symbols-outlined text-[13px] flex-shrink-0 ${isTask ? 'text-emerald-500' : 'text-indigo-400'}`}>
                {isTask ? 'check_box' : 'draft'}
              </span>
              <span className={`flex-1 text-xs font-medium truncate ${isTask ? 'text-emerald-700' : 'text-indigo-700'}`}>
                {psd.document_types?.name ?? '—'}
              </span>
              <button
                onClick={() => onRemoveDoc(psd.id)}
                disabled={!canDelete}
                className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                title={canDelete ? 'Remove item' : 'Each step must have at least one item'}
              >
                <span className="material-symbols-outlined text-[13px]">close</span>
              </button>
              {/* Deadline — for both documents and tasks */}
              <div className="flex flex-col items-end flex-shrink-0">
                <input
                  type="date"
                  value={psd.deadline_date ?? ''}
                  onChange={e => onUpdateDocDeadline(psd.id, e.target.value || null)}
                  className="px-2 py-1 text-xs border border-slate-200 rounded-lg text-slate-500 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 cursor-pointer w-32"
                  title={isTask ? 'Task deadline' : 'Document deadline'}
                />
                {psd.deadline_date && (
                  <span className={`text-[10px] mt-0.5 font-medium ${deadlineColor(days)}`}>
                    {deadlineLabel(days)}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Add doc + Add task row */}
        <div className="pl-8 pt-0.5 flex items-center gap-2 flex-wrap">
          <AddDocSelect  step={step} docTypes={docTypes}   onAdd={onAddDoc}  />
          <AddTaskSelect step={step} taskTypes={taskTypes} onAdd={onAddTask} />
        </div>
      </div>
    </div>
  );
}


/* ─── EMPTY STATE ────────────────────────────────────────────── */
// Shown when a template has no steps yet.
// Offers two paths: copy from another template OR start from scratch.
function EmptyState({ templates, applyTemplate, addStep, saving }) {
  const [selected,  setSelected]  = useState('');
  const [newLabel,  setNewLabel]  = useState('');

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    addStep(label);
    setNewLabel('');
  };

  return (
    <div className="p-6 space-y-5">

      {/* ── Start from scratch ─────────────────────────────── */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Start from scratch
        </p>
        <div className="flex items-center gap-2">
          <input
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="Name your first step…"
            className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] placeholder-slate-400"
          />
          <button
            onClick={handleAdd}
            disabled={!newLabel.trim() || saving}
            className="px-4 py-2 text-sm font-medium bg-[#4343d5] text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-40 flex items-center gap-1.5"
          >
            <span className="material-symbols-outlined text-[15px]">add</span>
            Add Step
          </button>
        </div>
      </div>

      {/* ── Copy from another template (only if others exist) ── */}
      {templates.length > 0 && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-xs text-slate-400">or copy from a template</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selected}
              onChange={e => setSelected(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] bg-white cursor-pointer"
            >
              <option value="">Choose a template to copy…</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <button
              onClick={() => { if (selected) applyTemplate(selected); }}
              disabled={!selected || saving}
              className="px-4 py-2 text-sm font-medium border border-indigo-200 text-indigo-600 rounded-xl hover:bg-indigo-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-[16px]">
                {saving ? 'hourglass_empty' : 'content_copy'}
              </span>
              {saving ? 'Copying…' : 'Copy Steps'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}


/* ─── STEP EDITOR ────────────────────────────────────────────── */
// Main configured state: list of steps + add + reset.
function StepEditor({ type, steps, templates, docTypes, taskTypes, saving, error,
                      applyTemplate, addStep, updateStep, deleteStep, moveStep,
                      addDocToStep, addTaskToStep, removeDocFromStep, updateDocDeadline }) {
  const [newLabel,      setNewLabel]      = useState('');
  const [resetTemplate, setResetTemplate] = useState('');
  const [confirmReset,  setConfirmReset]  = useState(false);

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    addStep(label);
    setNewLabel('');
  };

  const handleReset = () => {
    if (!resetTemplate) return;
    if (!confirmReset) { setConfirmReset(true); return; }
    applyTemplate(resetTemplate);
    setConfirmReset(false);
    setResetTemplate('');
  };

  return (
    <div className="p-6 space-y-3">

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          <span className="material-symbols-outlined text-[16px]">error</span>
          {error}
        </div>
      )}

      {/* Saving indicator */}
      {saving && (
        <div className="flex items-center gap-2 text-xs text-indigo-500">
          <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>
          Saving…
        </div>
      )}

      {/* Step list */}
      <div className="space-y-1.5">
        {steps.map((step, idx) => (
          <StepRow
            key={step.id}
            step={step}
            idx={idx}
            total={steps.length}
            docTypes={docTypes}
            taskTypes={taskTypes}
            onUpdate={updateStep}
            onDelete={deleteStep}
            onMove={moveStep}
            onAddDoc={addDocToStep}
            onAddTask={addTaskToStep}
            onRemoveDoc={removeDocFromStep}
            onUpdateDocDeadline={updateDocDeadline}
          />
        ))}
      </div>

      {/* Legend */}
      <p className="text-[11px] text-slate-400 pl-1">
        Click a step label to rename · Add documents to a step · Set a deadline per document — within 7 days becomes Urgent, within 14 days Action Needed
      </p>

      {/* Add step */}
      <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
        <input
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
          placeholder="New step name…"
          className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] placeholder-slate-400"
        />
        <button
          onClick={handleAdd}
          disabled={!newLabel.trim() || saving}
          className="px-4 py-2 text-sm font-medium bg-[#4343d5] text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-40 flex items-center gap-1.5"
        >
          <span className="material-symbols-outlined text-[15px]">add</span>
          Add Step
        </button>
      </div>

      {/* Reset to template */}
      {templates.length > 0 && (
        <div className="pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-400">Reset to template:</span>
          <select
            value={resetTemplate}
            onChange={e => { setResetTemplate(e.target.value); setConfirmReset(false); }}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none cursor-pointer"
          >
            <option value="">Choose template…</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {confirmReset ? (
            <button
              onClick={handleReset}
              disabled={!resetTemplate || saving}
              className="px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40"
            >
              Confirm — this will overwrite all current steps
            </button>
          ) : (
            <button
              onClick={handleReset}
              disabled={!resetTemplate || saving}
              className="px-3 py-1.5 text-xs font-medium border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
            >
              Reset
            </button>
          )}
        </div>
      )}
    </div>
  );
}


/* ─── TEMPLATE STEP PANEL ────────────────────────────────────── */
// Renders the step editor for a specific template.
// Separate component so each template gets its own hook instance.
function TemplateStepPanel({ templateId, type }) {
  const {
    steps, templates,
    loading, saving, error,
    applyTemplate, addStep, updateStep, deleteStep, moveStep,
    addDocToStep, addTaskToStep, removeDocFromStep, updateDocDeadline,
  } = useProcessConfig(templateId);

  const { docTypes, taskTypes } = useDocumentTypes();

  // Only show templates of the same direction as copy sources
  const sameTypeTemplates = templates.filter(t => t.type === type);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-400">
        <span className="material-symbols-outlined text-[16px] animate-spin">progress_activity</span>
        <span className="text-sm">Loading steps…</span>
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <EmptyState
        templates={sameTypeTemplates}
        applyTemplate={applyTemplate}
        addStep={addStep}
        saving={saving}
      />
    );
  }

  return (
    <StepEditor
      type={null}
      steps={steps}
      templates={sameTypeTemplates}
      docTypes={docTypes}
      taskTypes={taskTypes}
      saving={saving}
      error={error}
      applyTemplate={applyTemplate}
      addStep={addStep}
      updateStep={updateStep}
      deleteStep={deleteStep}
      moveStep={moveStep}
      addDocToStep={addDocToStep}
      addTaskToStep={addTaskToStep}
      removeDocFromStep={removeDocFromStep}
      updateDocDeadline={updateDocDeadline}
    />
  );
}


/* ─── TEMPLATE CARD ──────────────────────────────────────────── */
// Shows one template with header (name, degree badges, default) +
// expandable step editor.
function TemplateCard({ template, onUpdate, onDelete, onSetDefault, saving }) {
  const [expanded,    setExpanded]    = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameVal,     setNameVal]     = useState(template.name);
  const [showConfig,  setShowConfig]  = useState(false);
  const [confirmDel,  setConfirmDel]  = useState(false);

  // Draft state for the config panel — changes only write to DB on Save
  const [draft, setDraft] = useState({
    match_degree:     template.match_degree     ?? [],
    match_categories: template.match_categories ?? [],
  });
  const [configDirty, setConfigDirty] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  const openConfig = (e) => {
    e.stopPropagation();
    // Reset draft to current template values every time the panel opens
    setDraft({
      match_degree:     template.match_degree     ?? [],
      match_categories: template.match_categories ?? [],
    });
    setConfigDirty(false);
    setShowConfig(c => !c);
  };

  const handleNameBlur = () => {
    setEditingName(false);
    const trimmed = nameVal.trim();
    if (trimmed && trimmed !== template.name) onUpdate(template.id, { name: trimmed });
    else setNameVal(template.name);
  };

  const toggleDegree = (val) => {
    setDraft(d => {
      const next = d.match_degree.includes(val)
        ? d.match_degree.filter(v => v !== val)
        : [...d.match_degree, val];
      return { ...d, match_degree: next };
    });
    setConfigDirty(true);
  };

  const toggleCategory = (val) => {
    setDraft(d => {
      const next = d.match_categories.includes(val)
        ? d.match_categories.filter(v => v !== val)
        : [...d.match_categories, val];
      return { ...d, match_categories: next };
    });
    setConfigDirty(true);
  };

  const saveConfig = async () => {
    setConfigSaving(true);
    await onUpdate(template.id, {
      match_degree:     draft.match_degree,
      match_categories: draft.match_categories,
    });
    setConfigDirty(false);
    setConfigSaving(false);
  };

  const cancelConfig = () => {
    setDraft({
      match_degree:     template.match_degree     ?? [],
      match_categories: template.match_categories ?? [],
    });
    setConfigDirty(false);
    setShowConfig(false);
  };

  const degreeColors = {
    bachelor: 'bg-blue-100 text-blue-700',
    master:   'bg-indigo-100 text-indigo-700',
    mba:      'bg-violet-100 text-violet-700',
    other:    'bg-slate-100 text-slate-600',
  };

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">

      {/* ── Card header ────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 py-3 bg-slate-50/60 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Expand chevron */}
        <span className={`material-symbols-outlined text-[16px] text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}>
          chevron_right
        </span>

        {/* Name */}
        {editingName ? (
          <input
            autoFocus
            value={nameVal}
            onChange={e => setNameVal(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
            onClick={e => e.stopPropagation()}
            className="flex-1 px-2 py-0.5 text-sm font-semibold text-[#0b1c30] border border-indigo-300 rounded-lg focus:outline-none"
          />
        ) : (
          <span
            className="flex-1 text-sm font-semibold text-[#0b1c30]"
            onDoubleClick={e => { e.stopPropagation(); setEditingName(true); }}
            title="Double-click to rename"
          >
            {template.name}
          </span>
        )}

        {/* Degree badges */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {(template.match_degree ?? []).length === 0 ? (
            <span className="text-xs text-slate-400 italic">all students</span>
          ) : (
            (template.match_degree ?? []).map(d => (
              <span key={d} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${degreeColors[d] ?? 'bg-slate-100 text-slate-600'}`}>
                {DEGREE_LEVELS.find(l => l.value === d)?.label ?? d}
              </span>
            ))
          )}
        </div>

        {/* Default badge */}
        {template.is_default && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
            Default
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
          {/* Config toggle */}
          <button
            onClick={openConfig}
            className={`p-1.5 rounded-lg transition-colors ${showConfig ? 'bg-indigo-100 text-indigo-600' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
            title="Configure matching rules"
          >
            <span className="material-symbols-outlined text-[15px]">tune</span>
          </button>

          {/* Set default */}
          {!template.is_default && (
            <button
              onClick={() => onSetDefault(template.id)}
              className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-emerald-600 transition-colors"
              title="Set as default for this type"
            >
              <span className="material-symbols-outlined text-[15px]">star</span>
            </button>
          )}

          {/* Delete */}
          {confirmDel ? (
            <>
              <span className="text-xs text-red-500 font-medium">Delete?</span>
              <button onClick={() => setConfirmDel(false)} className="px-2 py-1 text-xs border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
              <button onClick={() => onDelete(template.id)} className="px-2 py-1 text-xs font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </>
          ) : (
            <button
              onClick={() => setConfirmDel(true)}
              className="p-1.5 rounded-lg text-slate-300 hover:bg-slate-100 hover:text-red-500 transition-colors"
              title="Delete template"
            >
              <span className="material-symbols-outlined text-[15px]">delete</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Matching rules config ────────────────────────────── */}
      {showConfig && (
        <div className="px-4 py-3 border-t border-slate-100 bg-white space-y-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Auto-match to students</p>

          {/* Degree level */}
          <div>
            <p className="text-xs text-slate-400 mb-1.5">Degree level <span className="text-slate-300">(empty = all students)</span></p>
            <div className="flex flex-wrap gap-1.5">
              {DEGREE_LEVELS.map(d => {
                const active = draft.match_degree.includes(d.value);
                return (
                  <button
                    key={d.value}
                    onClick={() => toggleDegree(d.value)}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                      active
                        ? `${degreeColors[d.value]} border-transparent`
                        : 'border-slate-200 text-slate-500 hover:border-indigo-300'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Student category */}
          <div>
            <p className="text-xs text-slate-400 mb-1.5">Exchange category <span className="text-slate-300">(empty = all categories)</span></p>
            <div className="flex flex-wrap gap-1.5">
              {STUDENT_CATEGORIES.map(c => {
                const active = draft.match_categories.includes(c.value);
                return (
                  <button
                    key={c.value}
                    onClick={() => toggleCategory(c.value)}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                      active
                        ? 'bg-indigo-100 text-indigo-700 border-transparent'
                        : 'border-slate-200 text-slate-500 hover:border-indigo-300'
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Save / Cancel */}
          <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
            <button
              onClick={saveConfig}
              disabled={!configDirty || configSaving}
              className="px-4 py-1.5 text-xs font-semibold bg-[#4343d5] text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            >
              {configSaving
                ? <><span className="material-symbols-outlined text-[13px] animate-spin">progress_activity</span>Saving…</>
                : <><span className="material-symbols-outlined text-[13px]">check</span>Save</>
              }
            </button>
            <button
              onClick={cancelConfig}
              disabled={configSaving}
              className="px-4 py-1.5 text-xs font-medium border border-slate-200 text-slate-500 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-colors"
            >
              Cancel
            </button>
            {configDirty && !configSaving && (
              <span className="text-[11px] text-amber-500 ml-1">Unsaved changes</span>
            )}
          </div>
        </div>
      )}

      {/* ── Step editor (expanded) ───────────────────────────── */}
      {expanded && (
        <div className="border-t border-slate-100">
          <TemplateStepPanel templateId={template.id} type={template.type} />
        </div>
      )}
    </div>
  );
}


/* ─── PROCESS PANEL ──────────────────────────────────────────── */
// Renders the list of templates for one direction (incoming/outgoing)
// filtered by the selected semester.
function ProcessPanel({ type, semesterId }) {
  const { templates, loading, saving, error, createTemplate, updateTemplate, deleteTemplate, setDefault } = useProcessTemplates();
  const [creating,    setCreating]    = useState(false);
  const [newName,     setNewName]     = useState('');
  const [copyFromId,  setCopyFromId]  = useState('');

  // Templates shown for current term+type
  const typeTemplates = templates.filter(t =>
    t.type === type && t.semester_id === semesterId
  );

  // All existing templates of the same type — deduplicated by name, deleted ones won't appear
  const copyOptions = templates
    .filter(t => t.type === type)
    .filter((t, idx, arr) => arr.findIndex(x => x.name === t.name) === idx);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    await createTemplate({
      name:        newName.trim(),
      type,
      semesterId,
      copyFromId:  copyFromId || null,
      isDefault:   typeTemplates.length === 0,
    });
    setNewName('');
    setCopyFromId('');
    setCreating(false);
  };

  const handleCancel = () => {
    setCreating(false);
    setNewName('');
    setCopyFromId('');
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center gap-2 text-slate-400">
        <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">

      {error && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          <span className="material-symbols-outlined text-[16px]">error</span>{error}
        </div>
      )}

      {/* Template list */}
      {typeTemplates.length === 0 && !creating ? (
        <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center">
          <span className="material-symbols-outlined text-slate-300 text-[36px]">route</span>
          <p className="text-sm font-medium text-slate-500 mt-2">No {type} process configured for this term</p>
          <p className="text-xs text-slate-400 mt-1">Create a process to define the steps for your {type} students.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {typeTemplates.map(t => (
            <TemplateCard
              key={t.id}
              template={t}
              onUpdate={updateTemplate}
              onDelete={deleteTemplate}
              onSetDefault={setDefault}
              saving={saving}
            />
          ))}
        </div>
      )}

      {/* Create new template */}
      {creating ? (
        <div className="space-y-2 pt-2">
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') handleCancel(); }}
              placeholder={`e.g. "Erasmus ${type === 'incoming' ? 'Incoming' : 'Outgoing'}"`}
              className="flex-1 px-3 py-2 text-sm border border-indigo-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-[#0b1c30] placeholder-slate-400"
            />
            <button
              onClick={handleCreate}
              disabled={!newName.trim() || saving}
              className="px-4 py-2 text-sm font-medium bg-[#4343d5] text-white rounded-xl hover:bg-indigo-700 disabled:opacity-40 transition-colors flex-shrink-0"
            >
              {saving ? 'Creating…' : 'Create'}
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-2 text-sm border border-slate-200 text-slate-500 rounded-xl hover:bg-slate-50 transition-colors flex-shrink-0"
            >
              Cancel
            </button>
          </div>

          {/* Copy from another term */}
          {copyOptions.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 flex-shrink-0">Copy steps from:</span>
              <select
                value={copyFromId}
                onChange={e => setCopyFromId(e.target.value)}
                className="flex-1 px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none cursor-pointer"
              >
                <option value="">— Start from scratch —</option>
                {copyOptions.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-dashed border-indigo-200 text-indigo-500 rounded-xl hover:bg-indigo-50 hover:border-indigo-300 transition-colors w-full justify-center"
        >
          <span className="material-symbols-outlined text-[16px]">add</span>
          New process
        </button>
      )}
    </div>
  );
}


/* ─── PROCESS CONFIG SECTION ─────────────────────────────────── */
// Root component — term selector + tab switcher (Incoming / Outgoing).
export default function ProcessConfigSection() {
  const [activeType,       setActiveType]       = useState('incoming');
  const [selectedSemester, setSelectedSemester] = useState('');

  const { semesters, loading: semestersLoading } = useSemesters();

  // Auto-select first semester when they load
  useState(() => {
    if (semesters.length > 0 && !selectedSemester) {
      setSelectedSemester(semesters[0].id);
    }
  });

  const noTerms = !semestersLoading && semesters.length === 0;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">

      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="material-symbols-outlined text-[18px] text-indigo-600">route</span>
          <h2 className="font-display text-base font-semibold text-[#0b1c30]">Exchange Process</h2>
        </div>

        <div className="flex items-center gap-3 flex-wrap justify-end">
          {/* Term selector */}
          {!semestersLoading && semesters.length > 0 && (
            <select
              value={selectedSemester}
              onChange={e => setSelectedSemester(e.target.value)}
              className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-600 bg-white focus:outline-none cursor-pointer"
            >
              <option value="">Select term…</option>
              {semesters.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}

          {/* Incoming / Outgoing tab switcher */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {[
              { value: 'incoming', label: '← Incoming' },
              { value: 'outgoing', label: 'Outgoing →' },
            ].map(tab => (
              <button
                key={tab.value}
                onClick={() => setActiveType(tab.value)}
                className={`px-4 py-1.5 text-xs font-medium transition-colors ${
                  activeType === tab.value
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* No terms warning */}
      {noTerms ? (
        <div className="p-8 text-center">
          <span className="material-symbols-outlined text-slate-300 text-[36px]">event_repeat</span>
          <p className="text-sm font-medium text-slate-500 mt-2">No terms created yet</p>
          <p className="text-xs text-slate-400 mt-1">
            Go to <strong>Exchange Terms</strong> (below) and create a term first —
            then come back here to configure its process.
          </p>
        </div>
      ) : !selectedSemester ? (
        <div className="p-8 text-center">
          <span className="material-symbols-outlined text-slate-300 text-[36px]">route</span>
          <p className="text-sm font-medium text-slate-500 mt-2">Select a term above</p>
          <p className="text-xs text-slate-400 mt-1">Choose a term to view and configure its process.</p>
        </div>
      ) : (
        <ProcessPanel type={activeType} semesterId={selectedSemester} />
      )}
    </div>
  );
}
